/**
 * general-chatbot.component.ts
 * ----------------------------
 * Standalone general guardrail chatbot page.
 * Completely independent of any assessment session.
 *
 * Features:
 *  - Streaming SSE responses with live typing effect
 *  - "New Chat" clears the conversation (no history sent)
 *  - Markdown-lite rendering via innerHTML
 *  - Auto-scroll to latest message
 */

import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule }   from '@angular/common';
import { FormsModule }    from '@angular/forms';
import { Subscription }   from 'rxjs';

import {
  GeneralChatService,
  GeneralChatMessage,
} from '../../core/services/general-chat.service';

interface ChatBubble {
  role:    'user' | 'assistant';
  text:    string;       // plain text for user; may contain markdown for assistant
  html:    string;       // rendered HTML shown in template
  loading: boolean;      // true while streaming
}

@Component({
  selector:    'app-general-chatbot',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './general-chatbot.component.html',
  styleUrl:    './general-chatbot.component.scss',
})
export class GeneralChatbotComponent implements OnDestroy {

  @ViewChild('scrollAnchor') private scrollAnchor!: ElementRef<HTMLDivElement>;

  messages = signal<ChatBubble[]>([
    {
      role:    'assistant',
      text:    "Hi there! 👋 I'm your CDAS General Guardrails Assistant. I can help you find specific guardrails, explain security requirements, and answer questions about IT, OT, and PCN compliance. What would you like to know?",
      html:    "Hi there! 👋 I'm your CDAS General Guardrails Assistant. I can help you find specific guardrails, explain security requirements, and answer questions about IT, OT, and PCN compliance. What would you like to know?",
      loading: false,
    },
  ]);

  query     = '';
  isSending = false;

  private _activeSub: Subscription | null = null;

  constructor(private readonly chatSvc: GeneralChatService) {}

  ngOnDestroy(): void {
    this._activeSub?.unsubscribe();
  }

  // -------------------------------------------------------------------------

  send(): void {
    const q = this.query.trim();
    if (!q || this.isSending) return;

    this.query    = '';
    this.isSending = true;

    // Add user bubble
    this._pushBubble({ role: 'user', text: q, html: q, loading: false });

    // Add empty assistant bubble (will be streamed into)
    const assistantIdx = this.messages().length;
    this._pushBubble({ role: 'assistant', text: '', html: '', loading: true });

    // Build history from current messages (excluding the new empty assistant bubble)
    const history: GeneralChatMessage[] = this.messages()
      .slice(0, assistantIdx)
      .filter(m => !m.loading)
      .map(m => ({ role: m.role, text: m.text }));

    this._activeSub = this.chatSvc
      .streamMessage(q, history)
      .subscribe({
        next:     delta  => this._appendDelta(assistantIdx, delta),
        error:    _err   => this._finishStream(assistantIdx, '⚠️ An error occurred. Please try again.'),
        complete: ()     => this._finishStream(assistantIdx),
      });
  }

  newChat(): void {
    this._activeSub?.unsubscribe();
    this._activeSub = null;
    this.isSending  = false;
    this.query      = '';
    this.messages.set([
      {
        role:    'assistant',
        text:    "Hi there! 👋 I'm your CDAS General Guardrails Assistant. I can help you find specific guardrails, explain security requirements, and answer questions about IT, OT, and PCN compliance. What would you like to know?",
        html:    "Hi there! 👋 I'm your CDAS General Guardrails Assistant. I can help you find specific guardrails, explain security requirements, and answer questions about IT, OT, and PCN compliance. What would you like to know?",
        loading: false,
      },
    ]);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private _pushBubble(bubble: ChatBubble): void {
    this.messages.update(msgs => [...msgs, bubble]);
    this._scrollToBottom();
  }

  private _appendDelta(idx: number, delta: string): void {
    this.messages.update(msgs => {
      const updated = [...msgs];
      const bubble  = { ...updated[idx] };
      bubble.text   += delta;
      bubble.html   = this._renderMarkdown(bubble.text);
      updated[idx]  = bubble;
      return updated;
    });
    this._scrollToBottom();
  }

  private _finishStream(idx: number, errorText?: string): void {
    this.messages.update(msgs => {
      const updated = [...msgs];
      const bubble  = { ...updated[idx] };
      if (errorText) {
        bubble.text  = errorText;
        bubble.html  = errorText;
      }
      bubble.loading = false;
      updated[idx]   = bubble;
      return updated;
    });
    this.isSending = false;
    this._scrollToBottom();
  }

  private _scrollToBottom(): void {
    setTimeout(() => {
      try {
        this.scrollAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
      } catch { /* ignore */ }
    }, 0);
  }

  /** Very lightweight markdown → HTML (bold, headers, bullets, line breaks). */
  private _renderMarkdown(text: string): string {
    return text
      // ### heading
      .replace(/^###\s+(.+)$/gm, '<h4>$1</h4>')
      // **bold**
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // bullet list
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // wrap consecutive <li> in <ul>
      .replace(/(<li>.*<\/li>\n?)+/gs, match => `<ul>${match}</ul>`)
      // double newline → paragraph break
      .replace(/\n\n/g, '</p><p>')
      // single newline → <br>
      .replace(/\n/g, '<br>')
      // wrap in paragraph
      .replace(/^(.+)$/, '<p>$1</p>');
  }
}
